// services/api-error-handler.ts
import axios, { AxiosError } from 'axios';
import { toast } from 'sonner'; // Assuming you're using shadcn/ui sonner for toasts

// Custom error type to handle API errors
export interface ApiErrorResponse {
  message: string;
  error_code?: string;
  details?: Record<string, any>;
}

export const handleApiError = (error: unknown) => {
  if (error instanceof AxiosError) {
    if (error.response) {
      const { status, data } = error.response;
      const message = (data as ApiErrorResponse)?.message || 'An error occurred';
      
      switch (status) {
        case 400:
          toast.error('Bad Request', { description: message });
          break;
        case 401:
          toast.error('Unauthorized', { description: 'Please log in again' });
          break;
        case 403:
          toast.error('Forbidden', { description: 'You do not have permission' });
          break;
        case 404:
          toast.error('Not Found', { description: message });
          break;
        case 500:
          toast.error('Server Error', { description: 'Please try again later' });
          break;
        default:
          toast.error('Error', { description: message });
      }
    } else if (error.request) {
      toast.error('Network Error', { description: 'Unable to connect to server' });
    } else {
      toast.error('Error', { description: error.message });
    }
  } else {
    toast.error('Error', { description: 'An unexpected error occurred' });
  }
  
  return Promise.reject(error);
};

// Utility function to check if a specific error type occurred
export const isApiErrorWithCode = (
  error: unknown, 
  errorCode: string
): error is AxiosError<ApiErrorResponse> => {
  return (
    axios.isAxiosError(error) && 
    error.response?.data?.error_code === errorCode
  );
};

// Example usage in an async function
/*
async function fetchData() {
  try {
    const response = await apiService.get('/some-endpoint');
    return response.data;
  } catch (error) {
    handleApiError(error);
  }
}
*/